import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../utils/utils';
import { getAuth } from 'firebase/auth';
import {
  CustomerInfo,
  CustomerInfoResponse,
  PaymentHistoryResponse,
  TotalVerificationsResponse,
} from './types';

export const paymentsApi = createApi({
  reducerPath: 'paymentsApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${getApiUrl()}/api/subscription/` }),
  tagTypes: ['payment', 'customerInfo', 'verifications'],
  endpoints: (builder) => ({
    // Verification-related queries
    getTotalVerifications: builder.query<TotalVerificationsResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const auth = getAuth();
        const user = auth?.currentUser;
        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }
        const token = await user.getIdToken();
        const result = await baseQuery({
          url: `total-verifications/`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as TotalVerificationsResponse };
      },
      providesTags: ['verifications'],
    }),

    // Customer payment-related queries
    getCustomerPayments: builder.query<PaymentHistoryResponse, string>({
      async queryFn(customerId, _queryApi, _extraOptions, fetchWithBQ) {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }

        const idToken = await user.getIdToken();

        const result = await fetchWithBQ({
          url: `customer/${customerId}/payments?generateReceipt=true`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as PaymentHistoryResponse };
      },
      providesTags: ['payment'],
    }),

    // Saved card info-related queries
    getSavedCardInfo: builder.query({
      query: (customerId) => `customer/${customerId}/saved-card-info`,
      providesTags: ['payment'],
    }),

    // Subscription-related mutations
    createSubscriptionCheckoutSession: builder.mutation<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }
        const idToken = await user.getIdToken();
        const result = await fetchWithBQ({
          url: 'create-subscription-checkout-session',
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as any };
      },
      invalidatesTags: ['payment', 'customerInfo', 'verifications'],
    }),

    // Customer info-related queries and mutations
    getCustomerInfo: builder.query<CustomerInfoResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }

        const idToken = await user.getIdToken();

        const result = await fetchWithBQ({
          url: 'customer/info',
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as CustomerInfoResponse };
      },
      providesTags: ['customerInfo'],
    }),

    // Customer-related mutations
    createCustomer: builder.mutation<
      CustomerInfoResponse,
      Omit<
        CustomerInfo,
        | 'id'
        | 'uid'
        | 'createdAt'
        | 'updatedAt'
        | 'isPaidCustomer'
        | 'payments'
        | 'customerId'
      >
    >({
      async queryFn(customerData, _queryApi, _extraOptions, fetchWithBQ) {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }

        const idToken = await user.getIdToken();

        const result = await fetchWithBQ({
          url: 'create',
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(customerData),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as CustomerInfoResponse };
      },
      invalidatesTags: ['customerInfo', 'payment', 'verifications'],
    }),

    // Payment status check
    checkPaymentStatus: builder.query<{ status: string }, string>({
      async queryFn(sessionId, _queryApi, _extraOptions, fetchWithBQ) {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          return { error: { status: 401, data: 'User not authenticated' } };
        }

        const idToken = await user.getIdToken();

        const result = await fetchWithBQ({
          url: `check-payment-status/${sessionId}`,
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        return { data: result.data as { status: string } };
      },
      providesTags: ['payment'],
    }),
  }),
});

export const {
  useCheckPaymentStatusQuery,
  useGetTotalVerificationsQuery,
  useGetCustomerPaymentsQuery,
  useGetSavedCardInfoQuery,
  useCreateSubscriptionCheckoutSessionMutation,
  useGetCustomerInfoQuery,
  useCreateCustomerMutation,
} = paymentsApi;
